import React from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";

function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
}

function resolveResponsive(value, { isSmUp, isMdUp, isLgUp, isXlUp }) {
    if (value == null || typeof value !== "object") return value;
    if (isXlUp && value.xl != null) return value.xl;
    if (isLgUp && value.lg != null) return value.lg;
    if (isMdUp && value.md != null) return value.md;
    if (isSmUp && value.sm != null) return value.sm;
    if (value.xs != null) return value.xs;
    const first = ["xs", "sm", "md", "lg", "xl"].find((k) => value[k] != null);
    return first ? value[first] : undefined;
}

function getViewportHeight() {
    if (typeof window === "undefined") return 800;
    return window.visualViewport?.height || window.innerHeight || 800;
}

export default function ScrollSettleIn({
                                           children,
                                           minOpacity = 0,
                                           startBeforePx,
                                           endAtPx = 120,
                                           startOffsetPx,
                                           endAtVh = null,
                                           disabled = false,
                                           sx,
                                       }) {
    const theme = useTheme();
    const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
    const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
    const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
    const isXlUp = useMediaQuery(theme.breakpoints.up("xl"));

    const hostRef = React.useRef(null);
    const innerRef = React.useRef(null);

    const lastRef = React.useRef({ opacity: null, ty: null });
    const vhRef = React.useRef(getViewportHeight());

    // Keep vh updated WITHOUT rerenders
    React.useEffect(() => {
        if (typeof window === "undefined") return;

        const vv = window.visualViewport;
        const update = () => {
            vhRef.current = getViewportHeight();
        };

        update();
        window.addEventListener("resize", update);
        vv?.addEventListener("resize", update);

        return () => {
            window.removeEventListener("resize", update);
            vv?.removeEventListener("resize", update);
        };
    }, []);

    React.useEffect(() => {
        const node = innerRef.current;
        if (!node) return;

        if (disabled) {
            node.style.opacity = "1";
            node.style.transform = "none";
            return;
        }

        let raf = 0;

        const bpCtx = { isSmUp, isMdUp, isLgUp, isXlUp };

        const apply = (opacity, translateY) => {
            const last = lastRef.current;
            if (
                last.opacity != null &&
                Math.abs(last.opacity - opacity) < 0.003 &&
                last.ty != null &&
                Math.abs(last.ty - translateY) < 0.5
            ) {
                return;
            }

            lastRef.current = { opacity, ty: translateY };
            node.style.opacity = String(opacity);
            node.style.transform = `translate3d(0, ${translateY}px, 0)`;
        };

        const measure = () => {
            raf = 0;
            const host = hostRef.current;
            if (!host) return;

            const vh = vhRef.current;

            const startBefore = resolveResponsive(startBeforePx, bpCtx) ?? 0;
            const startOffset = resolveResponsive(startOffsetPx, bpCtx) ?? 0;

            const endAtRatio =
                endAtVh != null ? resolveResponsive(endAtVh, bpCtx) : null;

            const endAtResolved =
                endAtRatio != null
                    ? Math.round(vh * Number(endAtRatio))
                    : (resolveResponsive(endAtPx, bpCtx) ?? 0);

            const rect = host.getBoundingClientRect();
            const startLine = vh + startBefore;
            const safeEndLine = Math.min(endAtResolved, startLine - 1);

            if (rect.top > startLine) {
                apply(minOpacity, -startOffset);
                return;
            }

            const denom = Math.max(1, startLine - safeEndLine);
            const progress = clamp((startLine - rect.top) / denom, 0, 1);

            const opacity = minOpacity + progress * (1 - minOpacity);
            const translateY = -(1 - progress) * startOffset;

            apply(opacity, translateY);
        };

        const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(measure);
        };

        // initial
        measure();

        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);

        const vv = window.visualViewport;
        vv?.addEventListener("scroll", onScroll, { passive: true });

        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
            vv?.removeEventListener("scroll", onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [
        disabled,
        minOpacity,
        startBeforePx,
        startOffsetPx,
        endAtPx,
        endAtVh,
        isSmUp,
        isMdUp,
        isLgUp,
        isXlUp,
    ]);

    return (
        <Box
            ref={hostRef}
            sx={{
                ...sx,
                willChange: "opacity, transform",
                transform: "translateZ(0)",
                zIndex: 0,
            }}
        >
            <Box
                ref={innerRef}
                sx={{
                    willChange: "opacity, transform",
                    backfaceVisibility: "hidden",
                    WebkitFontSmoothing: "antialiased",
                }}
            >
                {children}
            </Box>
        </Box>
    );
}
